export type ApiErrorPayload = {
  error?: string | { code?: string; message?: string; details?: Record<string, unknown> };
  detail?: string;
};

export type ApiErrorEventDetail = {
  message: string;
  code: string;
  status: number;
  details: Record<string, unknown>;
  payload: ApiErrorPayload;
  error: ApiRequestError;
};

let csrfToken = "";
let bearerToken = "";

export function setCsrfToken(token: string) {
  csrfToken = token || "";
}

export function setBearerToken(token: string) {
  bearerToken = token || "";
}

export function authHeaders(extra: HeadersInit = {}): HeadersInit {
  const headers = new Headers(extra);
  if (bearerToken) headers.set("Authorization", `Bearer ${bearerToken}`);
  if (csrfToken) headers.set("X-CSRF-Token", csrfToken);
  return headers;
}

export function errorMessage(payload: ApiErrorPayload, fallback: string): string {
  if (typeof payload.error === "string") return payload.error;
  if (payload.error && typeof payload.error === "object") return payload.error.message || payload.error.code || fallback;
  return payload.detail || fallback;
}

export class ApiRequestError extends Error {
  status: number;
  code: string;
  payload: ApiErrorPayload;
  details: Record<string, unknown>;

  constructor(message: string, status: number, code: string, payload: ApiErrorPayload) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
    this.payload = payload;
    this.details = errorDetails(payload);
  }
}

function errorCode(payload: ApiErrorPayload): string {
  if (payload.error && typeof payload.error === "object") return payload.error.code || "";
  if (typeof payload.error === "string") return payload.error;
  return "";
}

function errorDetails(payload: ApiErrorPayload): Record<string, unknown> {
  if (payload.error && typeof payload.error === "object" && payload.error.details) {
    return payload.error.details;
  }
  return {};
}

function notifyApiError(error: ApiRequestError) {
  const detail: ApiErrorEventDetail = {
    message: error.message,
    code: error.code,
    status: error.status,
    details: error.details,
    payload: error.payload,
    error
  };
  window.dispatchEvent(new CustomEvent("qqpb:api-error", { detail }));
}

function notifyAuthError(error: ApiRequestError) {
  if (error.status === 401 && error.code === "auth_required") {
    window.dispatchEvent(new CustomEvent("qqpb:auth-state", { detail: { state: "required", error } }));
  } else if (error.status === 423 && error.code === "admin_locked") {
    window.dispatchEvent(new CustomEvent("qqpb:auth-state", { detail: { state: "locked", error } }));
  } else if (error.status === 403 && error.code === "csrf_required") {
    window.dispatchEvent(new CustomEvent("qqpb:auth-state", { detail: { state: "csrf", error } }));
  }
}

async function raiseApiError(res: Response, payload: ApiErrorPayload, fallback: string): Promise<never> {
  const message = errorMessage(payload, fallback);
  const error = new ApiRequestError(message, res.status, errorCode(payload), payload);
  notifyApiError(error);
  notifyAuthError(error);
  throw error;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = authHeaders(init.headers || {});
  const res = await fetch(path, { ...init, credentials: "same-origin", headers });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({} as ApiErrorPayload));
    await raiseApiError(res, payload, res.statusText);
  }
  return res.json() as Promise<T>;
}

export async function textApi(path: string, init: RequestInit = {}): Promise<string> {
  const headers = authHeaders(init.headers || {});
  const res = await fetch(path, { ...init, credentials: "same-origin", headers });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({} as ApiErrorPayload));
    await raiseApiError(res, payload, res.statusText);
  }
  return res.text();
}

export function postJson<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
}

export function putJson<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
}
